import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { usePortalChat, conversationDisplayTime } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import {
  CONVERSATION_ROW_HEIGHT,
  GROUP_HEADER_ROW_HEIGHT,
  DIRECTORY_SEARCH_DEBOUNCE_MS,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import {
  getChatDirectoryRequest,
  createConversationRequest,
  deleteSectionRequest,
  type ChatConversation,
  type ChatDirectoryUser,
} from "../api/portalChat.api";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import {
  NewChatDialog,
  SectionsDialog,
  ProjectOrderDialog,
  HideConfirmDialog,
  NewSectionDialog,
} from "./ChatListDialogs";

function previewText(conversation: ChatConversation, deletedText: string, attachmentText: string): string {
  const last = conversation.lastMessage;
  if (!last) return "";
  if (last.deleted) return deletedText;
  if (last.kind === "system") return last.text;
  const body = last.text || (last.hasAttachments ? attachmentText : "");
  return `${last.senderName}: ${body}`;
}

function RowAvatar({ conversation }: { readonly conversation: ChatConversation }) {
  if (conversation.type === "direct" && conversation.otherUser) {
    return <UserNameTagComponent showAvatarOnly id={String(conversation.otherUser._id)} size="XS" isCircle />;
  }
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e8f1fc]">
      <IconComponent name={conversation.type === "space" ? "Grouping" : "usersIcon"} width="1rem" height="1rem" />
    </span>
  );
}

type ListGroup = {
  key: string;
  label: string | null;
  rows: ChatConversation[];
  /** Section groups collapse and carry a header menu; type/project headers carry their own. */
  sectionId?: string;
  emptyHint?: boolean;
  /** Set on All-Chats type headers — they get the PRD §4.9/§4.10 category-header ••• menu. */
  typeHeader?: "direct" | "group" | "space";
};

/** Left pane: scope dropdown, search (chats + people + hidden), unread filter, sections, rows. */
function ChatListPane() {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const { scope, unreadOnly } = chat;
  const [search, setSearch] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [showProjectOrder, setShowProjectOrder] = useState(false);
  const [hideCandidate, setHideCandidate] = useState<ChatConversation | null>(null);
  const [menuFor, setMenuFor] = useState<{ conversation: ChatConversation; anchor: HTMLElement } | null>(null);
  const [sectionMenuAnchor, setSectionMenuAnchor] = useState<HTMLElement | null>(null);
  const [sectionHeaderMenu, setSectionHeaderMenu] = useState<{ sectionId: string; anchor: HTMLElement } | null>(null);
  const [typeHeaderMenu, setTypeHeaderMenu] = useState<{
    type: "direct" | "group" | "space";
    anchor: HTMLElement;
  } | null>(null);
  const [newSectionFor, setNewSectionFor] = useState<{
    scope: "direct" | "group";
    assignConversationId?: string;
  } | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [createError, setCreateError] = useState(false);
  const [peopleResults, setPeopleResults] = useState<ChatDirectoryUser[]>([]);
  // PRD §4.8: rows that qualified while the unread filter is ON stay visible after being read,
  // until the filter is toggled off and on again.
  const unreadStickyIds = useRef<Set<string>>(new Set());

  // PRD §4.7: switching scope (now owned by the panel header) clears any active search.
  useEffect(() => {
    setSearch("");
    setCreateError(false);
  }, [scope]);

  // PRD §4.6: open menus close when the panel is maximised or restored.
  useEffect(() => {
    setMenuFor(null);
    setSectionMenuAnchor(null);
    setSectionHeaderMenu(null);
    setTypeHeaderMenu(null);
  }, [chat.menuEpoch]);

  const searchLower = search.trim().toLowerCase();

  // PRD §4.2/§4.11: search also surfaces people you can start a DM with.
  useEffect(() => {
    if (!searchLower || scope === "project") {
      setPeopleResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      getChatDirectoryRequest({ search: search.trim() })
        .then((response) => {
          if (!cancelled && response.data.status) setPeopleResults((response.data.result ?? []).slice(0, 5));
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }, DIRECTORY_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchLower, search, scope]);

  const scoped = useMemo(() => {
    const rows = chat.conversations.filter((c) => {
      if (scope === "direct" && c.type !== "direct") return false;
      if (scope === "group" && c.type !== "group") return false;
      if (scope === "project" && c.type !== "space") return false;
      return true;
    });
    // Keep list order live: new activity floats a row to the top without a refetch.
    return [...rows].sort((a, b) => (b.lastMessageDate ?? b.createdDate) - (a.lastMessageDate ?? a.createdDate));
  }, [chat.conversations, scope]);

  const matchesSearch = useCallback(
    (c: ChatConversation) => {
      if (!searchLower) return true;
      if (c.name.toLowerCase().includes(searchLower)) return true;
      if (c.projectName?.toLowerCase().includes(searchLower)) return true;
      if (c.lastMessage?.text.toLowerCase().includes(searchLower)) return true;
      const section = chat.sections.find((s) => String(s._id) === String(c.sectionId ?? ""));
      return Boolean(section?.name.toLowerCase().includes(searchLower));
    },
    [searchLower, chat.sections],
  );

  const visible = useMemo(() => {
    // PRD §4.10/§4.11: hidden project chats resurface under Current Project while the user is
    // working in that project (flagged); every other hidden chat is excluded from all views.
    let rows = scoped.filter(
      (c) =>
        !c.hidden ||
        (scope === "project" && chat.currentProjectId && String(c.projectId ?? "") === chat.currentProjectId),
    );
    rows = rows.filter(matchesSearch);
    if (unreadOnly) {
      rows = rows.filter((c) => !c.hidden); // hidden chats never appear under the unread filter
      for (const c of rows) if (c.unreadCount > 0) unreadStickyIds.current.add(c._id);
      rows = rows.filter((c) => c.unreadCount > 0 || unreadStickyIds.current.has(c._id));
    }
    return rows;
  }, [scoped, unreadOnly, matchesSearch, scope, chat.currentProjectId]);

  const hiddenMatches = useMemo(
    () =>
      searchLower
        ? scoped
            .filter(
              (c) =>
                c.hidden &&
                // Already resurfaced into the Current Project group — don't list it twice.
                !(scope === "project" && chat.currentProjectId && String(c.projectId ?? "") === chat.currentProjectId),
            )
            .filter(matchesSearch)
        : [],
    [scoped, searchLower, matchesSearch, scope, chat.currentProjectId],
  );

  const toggleUnreadOnly = () => {
    unreadStickyIds.current = new Set();
    chat.toggleUnreadOnly();
  };

  const typeLabel = useCallback(
    (type: ChatConversation["type"]) =>
      type === "direct" ? t("Direct Chats") : type === "group" ? t("Group Chats") : t("Project Chats"),
    [t],
  );

  const groupByProject = useCallback(
    (rows: ChatConversation[]): ListGroup[] => {
      const byProject = new Map<string, ChatConversation[]>();
      for (const c of rows) {
        const key = String(c.projectId ?? "");
        byProject.set(key, [...(byProject.get(key) ?? []), c]);
      }
      const order = [...byProject.keys()].sort((a, b) => {
        if (chat.currentProjectId) {
          if (a === chat.currentProjectId) return -1;
          if (b === chat.currentProjectId) return 1;
        }
        const ia = chat.projectOrder.indexOf(a);
        const ib = chat.projectOrder.indexOf(b);
        if (ia !== -1 || ib !== -1) {
          return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
        }
        const nameA = byProject.get(a)?.[0]?.projectName ?? "";
        const nameB = byProject.get(b)?.[0]?.projectName ?? "";
        return nameA.localeCompare(nameB);
      });
      return order.map((projectId) => ({
        key: `project-${projectId}`,
        label:
          projectId === chat.currentProjectId
            ? `${t("Current Project")} · ${byProject.get(projectId)?.[0]?.projectName ?? ""}`
            : (byProject.get(projectId)?.[0]?.projectName ?? t("Project")),
        rows: byProject.get(projectId) ?? [],
      }));
    },
    [chat.currentProjectId, chat.projectOrder, t],
  );

  const groupBySections = useCallback(
    (rows: ChatConversation[], sectionScope: "direct" | "group"): ListGroup[] => {
      const scopeSections = chat.sections.filter((s) => s.scope === sectionScope).sort((a, b) => a.order - b.order);
      const inSection = new Map<string, ChatConversation[]>();
      const unsectioned: ChatConversation[] = [];
      for (const c of rows) {
        const sectionId = c.sectionId ? String(c.sectionId) : null;
        if (sectionId && scopeSections.some((s) => String(s._id) === sectionId)) {
          inSection.set(sectionId, [...(inSection.get(sectionId) ?? []), c]);
        } else {
          unsectioned.push(c);
        }
      }
      const result: ListGroup[] = [{ key: `${sectionScope}-unsectioned`, label: null, rows: unsectioned }];
      for (const section of scopeSections) {
        const sectionRows = inSection.get(String(section._id)) ?? [];
        // PRD §4.8: sections with no unread chats collapse away entirely under the filter.
        if (unreadOnly && !sectionRows.length) continue;
        if (searchLower && !sectionRows.length && !section.name.toLowerCase().includes(searchLower)) continue;
        result.push({
          key: `section-${String(section._id)}`,
          label: section.name,
          rows: sectionRows,
          sectionId: String(section._id),
          emptyHint: !sectionRows.length,
        });
      }
      return result;
    },
    [chat.sections, unreadOnly, searchLower],
  );

  const groups = useMemo((): ListGroup[] => {
    if (scope === "project") return groupByProject(visible);
    if (scope === "direct" || scope === "group") return groupBySections(visible, scope);
    // All Chats (PRD §4.7/§4.9): type headers, with each type's sections nested beneath it.
    const result: ListGroup[] = [];
    for (const type of ["direct", "group", "space"] as const) {
      const rows = visible.filter((c) => c.type === type);
      if (!rows.length) continue;
      if (type === "space") {
        result.push({ key: "type-space", label: typeLabel(type), rows, typeHeader: "space" });
        continue;
      }
      const nested = groupBySections(rows, type);
      // The unsectioned block carries the type header; the section blocks follow beneath it.
      result.push({ key: `type-${type}`, label: typeLabel(type), rows: nested[0]?.rows ?? [], typeHeader: type });
      result.push(...nested.slice(1));
    }
    return result;
  }, [scope, visible, groupByProject, groupBySections, typeLabel]);

  /** Flat row model for the virtualized list: headers, hints and rows in render order. */
  type ListRowModel =
    | { kind: "sectionHeader"; key: string; label: string; sectionId: string; count: number; collapsed: boolean }
    | { kind: "typeHeader"; key: string; label: string; typeHeader?: "direct" | "group" | "space" }
    | { kind: "conversation"; key: string; conversation: ChatConversation; hiddenBadge: boolean }
    | { kind: "emptyHint"; key: string }
    | { kind: "person"; key: string; user: ChatDirectoryUser };

  const listRows = useMemo((): ListRowModel[] => {
    const rows: ListRowModel[] = [];
    for (const group of groups) {
      const collapsed = group.sectionId !== undefined && collapsedSections.has(group.sectionId);
      if (group.label !== null) {
        if (group.sectionId !== undefined) {
          rows.push({
            kind: "sectionHeader",
            key: group.key,
            label: group.label,
            sectionId: group.sectionId,
            count: group.rows.length,
            collapsed,
          });
        } else {
          rows.push({ kind: "typeHeader", key: group.key, label: group.label, typeHeader: group.typeHeader });
        }
      }
      if (!collapsed) {
        for (const conversation of group.rows) {
          rows.push({ kind: "conversation", key: conversation._id, conversation, hiddenBadge: conversation.hidden });
        }
        if (group.emptyHint && !unreadOnly && !searchLower) rows.push({ kind: "emptyHint", key: `${group.key}-hint` });
      }
    }
    if (hiddenMatches.length) {
      rows.push({ kind: "typeHeader", key: "hidden-header", label: t("Hidden Chats") });
      for (const conversation of hiddenMatches) {
        rows.push({ kind: "conversation", key: `hidden-${conversation._id}`, conversation, hiddenBadge: true });
      }
    }
    if (peopleResults.length) {
      rows.push({ kind: "typeHeader", key: "people-header", label: t("People") });
      for (const user of peopleResults) rows.push({ kind: "person", key: `person-${String(user._id)}`, user });
    }
    return rows;
  }, [groups, collapsedSections, unreadOnly, searchLower, hiddenMatches, peopleResults, t]);

  const renderListRow = (row: ListRowModel) => {
    if (row.kind === "conversation") return renderRow(row.conversation, { hiddenBadge: row.hiddenBadge });
    if (row.kind === "person") {
      return (
        <button
          type="button"
          onClick={() => void startDirectWith(row.user)}
          className="flex h-full w-full items-center gap-2 px-3 py-2 text-start hover:bg-[#f0f5f9]"
        >
          <UserNameTagComponent showAvatarOnly id={String(row.user._id)} size="XS" isCircle />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">{row.user.fullName}</span>
            <span className="block truncate text-xs text-gray-400">{row.user.email}</span>
          </span>
        </button>
      );
    }
    if (row.kind === "emptyHint") {
      return (
        <div className="px-3 py-1 text-xs italic text-gray-400">
          {t("No chats here yet — move one in from a chat's ••• menu.")}
        </div>
      );
    }
    if (row.kind === "sectionHeader") {
      return (
        <div className="flex h-full items-center justify-between bg-white px-3">
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-400"
            onClick={() =>
              setCollapsedSections((prev) => {
                const next = new Set(prev);
                if (next.has(row.sectionId)) next.delete(row.sectionId);
                else next.add(row.sectionId);
                return next;
              })
            }
          >
            <IconComponent
              name="BottomArrow"
              width="0.6rem"
              height="0.6rem"
              className={row.collapsed ? "-rotate-90" : ""}
            />
            {row.label} ({row.count})
          </button>
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => setSectionHeaderMenu({ sectionId: row.sectionId, anchor: e.currentTarget })}
            onKeyDown={() => undefined}
          >
            <IconComponent name="threedots" width="0.85rem" height="0.85rem" />
          </span>
        </div>
      );
    }
    const rowTypeHeader = row.typeHeader;
    return (
      <div className="flex h-full items-center bg-white px-3">
        <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {row.label}
          {rowTypeHeader !== undefined && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => setTypeHeaderMenu({ type: rowTypeHeader, anchor: e.currentTarget })}
              onKeyDown={() => undefined}
            >
              <IconComponent name="threedots" width="0.8rem" height="0.8rem" />
            </span>
          )}
        </span>
      </div>
    );
  };

  const startDirectWith = async (user: ChatDirectoryUser) => {
    try {
      const response = await createConversationRequest({ type: "direct", memberIds: [user._id] });
      if (response.data.status && response.data.result) {
        setSearch("");
        await chat.refreshList();
        chat.selectConversation(response.data.result._id);
      } else if (!response.data.status) {
        chat.showServerError(response.data.errorMessage, t("Could not create the chat"));
      }
    } catch (error) {
      Sentry.captureException(error);
      chat.showToast(t("Could not create the chat"), false);
    }
  };

  const renderRow = (conversation: ChatConversation, options: { hiddenBadge?: boolean } = {}) => {
    const isSelected = chat.selectedId === conversation._id;
    const showHiddenBadge = options.hiddenBadge ?? conversation.hidden;
    const open = () => {
      // PRD §4.11: selecting a hidden chat from search un-hides it, opens it and clears the search.
      if (conversation.hidden && searchLower) {
        chat.unhideAndOpen(conversation._id);
        setSearch("");
        return;
      }
      chat.selectConversation(conversation._id);
    };
    return (
      <div
        key={conversation._id}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter") open();
        }}
        className={`group flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-[#f0f5f9] ${
          isSelected ? "bg-[#e8f1fc]" : ""
        }`}
      >
        <RowAvatar conversation={conversation} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className={`truncate text-sm ${conversation.unreadCount ? "font-semibold" : ""}`}>
              {conversation.name}
            </span>
            {conversation.muted && <IconComponent name="Volume1D" width="0.85rem" height="0.85rem" />}
            {showHiddenBadge && (
              <span className="rounded bg-gray-100 px-1 text-[0.6rem] text-gray-500">{t("Hidden")}</span>
            )}
          </div>
          <div className="truncate text-xs text-gray-500">
            {previewText(conversation, t("This message was deleted"), t("Attachment"))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[0.65rem] text-gray-400">
            {conversation.lastMessageDate ? conversationDisplayTime(conversation.lastMessageDate) : ""}
          </span>
          {/* PRD §4.5: a muted conversation still shows its in-row unread count. */}
          {conversation.unreadCount > 0 && (
            <span
              className={`rounded-full px-1.5 text-[0.65rem] font-semibold text-white ${
                conversation.muted ? "bg-gray-300" : "bg-[#6BBBE3]"
              }`}
            >
              {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
            </span>
          )}
        </div>
        <span
          className="invisible group-hover:visible"
          onClick={(e) => {
            e.stopPropagation();
            setMenuFor({ conversation, anchor: e.currentTarget });
          }}
          onKeyDown={(e) => e.stopPropagation()}
          role="button"
          tabIndex={-1}
        >
          <IconComponent name="threedots" width="1rem" height="1rem" toolTipContent={t("More")} />
        </span>
      </div>
    );
  };

  const renderEmptyState = () => {
    if (searchLower) {
      return (
        <div className="flex flex-col items-center gap-1 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No results for {{query}}", { query: search.trim() })}</span>
          <span className="text-xs">
            {t(
              "Tips: Conversation and section names · People you share a project with · Hidden chats — open one to bring it back",
            )}
          </span>
        </div>
      );
    }
    if (unreadOnly) {
      return (
        <div className="flex flex-col items-center gap-1 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No unread messages")}</span>
          <span className="text-xs">
            {t("You are up to date on every conversation here. Switch off Unread to see them all again.")}
          </span>
        </div>
      );
    }
    if (scope === "project") {
      return (
        <div className="flex flex-col items-center gap-1 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No project chats yet")}</span>
          <span className="text-xs">
            {t("Project chats come with each project space. You are added automatically when you join one.")}
          </span>
        </div>
      );
    }
    if (scope === "direct") {
      return (
        <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No direct chats yet")}</span>
          <span className="text-xs">{t("Start a one-to-one conversation with anyone on your projects.")}</span>
          <button type="button" className="text-[#1E88E5]" onClick={() => setShowNewChat(true)}>
            {t("New direct chat")}
          </button>
        </div>
      );
    }
    if (scope === "group") {
      return (
        <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No group chats yet")}</span>
          <span className="text-xs">{t("Bring a few people together around an RFI, a package or a handover.")}</span>
          <button type="button" className="text-[#1E88E5]" onClick={() => setShowNewChat(true)}>
            {t("New group chat")}
          </button>
        </div>
      );
    }
    // PRD §4.16 distinguishes "none in view" (chats exist but are all hidden) from a brand-new user.
    if (chat.conversations.length) {
      return (
        <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-gray-500">
          <span className="font-semibold">{t("No chats yet.")}</span>
          <span className="text-xs">{t("Start a direct or group chat with anyone on your projects.")}</span>
          <button type="button" className="text-[#1E88E5]" onClick={() => setShowNewChat(true)}>
            {t("New chat")}
          </button>
        </div>
      );
    }
    // Brand-new user start layout (PRD §4.16): two cards.
    return (
      <div className="flex flex-col gap-3 p-4">
        <button
          type="button"
          onClick={() => setShowNewChat(true)}
          className="rounded-lg border border-[#dce6f1] p-3 text-start hover:bg-[#f0f5f9]"
        >
          <div className="text-sm font-semibold">{t("Direct chat")}</div>
          <div className="text-xs text-gray-500">{t("One-to-one with anyone on your projects")}</div>
        </button>
        <button
          type="button"
          onClick={() => setShowNewChat(true)}
          className="rounded-lg border border-[#dce6f1] p-3 text-start hover:bg-[#f0f5f9]"
        >
          <div className="text-sm font-semibold">{t("Group chat")}</div>
          <div className="text-xs text-gray-500">{t("A few people around an RFI, package or handover")}</div>
        </button>
      </div>
    );
  };

  const menuConversation = menuFor?.conversation;

  return (
    <div className="flex w-[22rem] min-w-[18rem] flex-col border-e border-[#e8f1fc]">
      <div className="flex items-center gap-2 border-b border-[#e8f1fc] px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 rounded border border-[#dce6f1] px-2">
          <IconComponent name="SearchIcon" width="0.9rem" height="0.9rem" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={SEARCH_MAX_LENGTH}
            placeholder={t("Search")}
            className="w-full border-none py-1 text-sm outline-none"
          />
        </div>
        <button
          type="button"
          title={unreadOnly ? t("Showing unread only") : t("Show unread only")}
          onClick={toggleUnreadOnly}
          className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${
            unreadOnly ? "border-[#6BBBE3] bg-[#e8f1fc] font-semibold" : "border-[#dce6f1]"
          }`}
        >
          {t("Unread")}
        </button>
        {(scope === "direct" || scope === "group") && (
          <IconComponent
            name="sliderSettingIcon"
            width="1rem"
            height="1rem"
            toolTipContent={t("Manage sections")}
            onClick={() => setShowSections(true)}
          />
        )}
        {scope === "project" && (
          <IconComponent
            name="sliderSettingIcon"
            width="1rem"
            height="1rem"
            toolTipContent={t("Manage sections")}
            onClick={() => setShowProjectOrder(true)}
          />
        )}
        <IconComponent
          name="add-icon"
          width="1.1rem"
          height="1.1rem"
          toolTipContent={scope === "project" ? t("Project chats are created with the Space") : t("New chat")}
          onClick={() => {
            if (scope === "project") {
              setCreateError(true);
              return;
            }
            setShowNewChat(true);
          }}
        />
      </div>
      {createError && scope === "project" && (
        <div className="flex items-start justify-between gap-2 border-b border-[#f6dcdc] bg-[#fdf3f3] px-3 py-1.5 text-xs text-[#b3261e]">
          <span>
            {t(
              "Project chats cannot be created here. A chat is created with each Space, and you are added to it automatically when you join the Space.",
            )}
          </span>
          <span role="button" tabIndex={0} onClick={() => setCreateError(false)} onKeyDown={() => undefined}>
            <IconComponent name="CrossIcon" width="0.6rem" height="0.6rem" />
          </span>
        </div>
      )}

      <div className="min-h-0 flex-1">
        {chat.loadingList && !chat.conversations.length && (
          <div className="p-4 text-sm text-gray-500">{t("Loading")}…</div>
        )}
        {!chat.loadingList && !listRows.length && renderEmptyState()}
        {listRows.length > 0 && (
          <VirtualizedList
            items={listRows}
            ariaLabel={t("Chats")}
            rowHeight={(row) =>
              row.kind === "conversation" || row.kind === "person" ? CONVERSATION_ROW_HEIGHT : GROUP_HEADER_ROW_HEIGHT
            }
            renderRow={(row) => renderListRow(row)}
          />
        )}
      </div>

      {menuFor && menuConversation && (
        <MenuComponent anchorEl={menuFor.anchor} open onClose={() => setMenuFor(null)} width="14rem">
          {menuConversation.unreadCount > 0 && (
            <MenuItemComponent
              onClick={() => {
                setMenuFor(null);
                chat.markConversationRead(menuConversation._id);
              }}
            >
              {t("Mark as read")}
            </MenuItemComponent>
          )}
          <MenuItemComponent
            onClick={() => {
              setMenuFor(null);
              if (menuConversation.hidden) chat.unhideAndOpen(menuConversation._id);
              else setHideCandidate(menuConversation);
            }}
          >
            {menuConversation.hidden ? t("Unhide chat") : t("Hide conversation")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              setMenuFor(null);
              void chat.updateConversationState(menuConversation._id, { muted: !menuConversation.muted });
              chat.showToast(menuConversation.muted ? t("Chat unmuted") : t("Chat muted"));
            }}
          >
            {menuConversation.muted ? t("Unmute") : t("Mute")}
          </MenuItemComponent>
          {menuConversation.type !== "space" && (
            <MenuItemComponent onClick={(e) => setSectionMenuAnchor(e.currentTarget)}>
              {t("Move to section")}
            </MenuItemComponent>
          )}
        </MenuComponent>
      )}
      {menuConversation && sectionMenuAnchor && (
        <MenuComponent
          anchorEl={sectionMenuAnchor}
          open
          onClose={() => {
            setSectionMenuAnchor(null);
            setMenuFor(null);
          }}
          width="14rem"
        >
          <MenuItemComponent
            onClick={() => {
              void chat.updateConversationState(menuConversation._id, { sectionId: null });
              setSectionMenuAnchor(null);
              setMenuFor(null);
            }}
          >
            {t("No section")}
          </MenuItemComponent>
          {chat.sections
            .filter((s) => s.scope === menuConversation.type)
            .sort((a, b) => a.order - b.order)
            .map((section) => (
              <MenuItemComponent
                key={String(section._id)}
                onClick={() => {
                  void chat.updateConversationState(menuConversation._id, { sectionId: String(section._id) });
                  setSectionMenuAnchor(null);
                  setMenuFor(null);
                }}
              >
                {section.name}
              </MenuItemComponent>
            ))}
          <MenuItemComponent
            onClick={() => {
              setSectionMenuAnchor(null);
              setMenuFor(null);
              setNewSectionFor({
                scope: menuConversation.type === "group" ? "group" : "direct",
                assignConversationId: menuConversation._id,
              });
            }}
          >
            {t("Create new section")}
          </MenuItemComponent>
        </MenuComponent>
      )}
      {sectionHeaderMenu && (
        <MenuComponent
          anchorEl={sectionHeaderMenu.anchor}
          open
          onClose={() => setSectionHeaderMenu(null)}
          width="13rem"
        >
          <MenuItemComponent
            onClick={() => {
              setSectionHeaderMenu(null);
              setShowSections(true);
            }}
          >
            {t("Rename section")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              const sectionId = sectionHeaderMenu.sectionId;
              setSectionHeaderMenu(null);
              // PRD §4.9: deleting a section un-assigns its chats; it never deletes the chats.
              void deleteSectionRequest({ sectionId: toObjectIdString(sectionId) }).then(() => chat.refreshList());
            }}
          >
            {t("Delete section")}
          </MenuItemComponent>
        </MenuComponent>
      )}

      {typeHeaderMenu && (
        <MenuComponent anchorEl={typeHeaderMenu.anchor} open onClose={() => setTypeHeaderMenu(null)} width="13rem">
          {typeHeaderMenu.type === "space" ? (
            <MenuItemComponent
              onClick={() => {
                setTypeHeaderMenu(null);
                setShowProjectOrder(true);
              }}
            >
              {t("Manage sections")}
            </MenuItemComponent>
          ) : (
            <MenuItemComponent
              onClick={() => {
                const sectionScope = typeHeaderMenu.type === "group" ? "group" : "direct";
                setTypeHeaderMenu(null);
                setNewSectionFor({ scope: sectionScope });
              }}
            >
              {t("Create a section")}
            </MenuItemComponent>
          )}
        </MenuComponent>
      )}

      {showNewChat && <NewChatDialog onClose={() => setShowNewChat(false)} />}
      {showSections && (
        <SectionsDialog initialScope={scope === "group" ? "group" : "direct"} onClose={() => setShowSections(false)} />
      )}
      {showProjectOrder && <ProjectOrderDialog onClose={() => setShowProjectOrder(false)} />}
      {newSectionFor && (
        <NewSectionDialog
          scope={newSectionFor.scope}
          assignConversationId={newSectionFor.assignConversationId}
          onClose={() => setNewSectionFor(null)}
        />
      )}
      {hideCandidate && <HideConfirmDialog conversation={hideCandidate} onClose={() => setHideCandidate(null)} />}
    </div>
  );
}

export default ChatListPane;
